import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/** Admin → Client mappings — mirrors server/mappings.ts. */

export type MapProduct = 'rmm' | 'docs' | 'cipp' | 'backup' | 'sophos' | 'unifi' | 'domains' | 'csp' | 'huntress' | 'screenconnect';
export type MapSource = 'vendor' | 'auto' | 'manual' | 'ignored' | 'none';

export interface MapRecord {
  product: MapProduct;
  id: string;
  name: string;
  detail: string | null;
  companyId: number | null;
  companyName: string | null;
  source: MapSource;
  sourceNote: string | null;
  editable: boolean;
  fixUrl: string | null;
  suggestion: { companyId: number; companyName: string; strong: boolean; reason: string } | null;
  resettable: boolean;
}

export interface ProductMeta { id: MapProduct; label: string; unit: string; owner: 'vendor' | 'cosmos'; available: boolean; error: string | null }

export type MapValue = { companyId: number; companyName: string } | { ignored: true } | null;

export function useMappings() {
  return useQuery({
    queryKey: ['admin', 'mappings'],
    queryFn: () => api.get<{ products: ProductMeta[]; records: MapRecord[] }>('/api/admin/mappings'),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

/** Every module reads these links, so refresh everything that shows a company after a change. */
function useInvalidateMapped() {
  const qc = useQueryClient();
  return () => {
    for (const key of [['admin', 'mappings'], ['insights'], ['cipp'], ['backup'], ['network'], ['domains'], ['csp']]) qc.invalidateQueries({ queryKey: key });
  };
}

export function useSetMapping() {
  const invalidate = useInvalidateMapped();
  return useMutation({
    mutationFn: ({ product, id, value }: { product: MapProduct; id: string; value: MapValue }) =>
      api.put<{ ok: true }>(`/api/admin/mappings/${product}/${encodeURIComponent(id)}`, { value }),
    onSuccess: invalidate,
  });
}

export function useAcceptMappings() {
  const invalidate = useInvalidateMapped();
  return useMutation({
    mutationFn: (items: { product: MapProduct; id: string; companyId: number; companyName: string }[]) =>
      api.post<{ saved: number }>('/api/admin/mappings/accept', { items }),
    onSuccess: invalidate,
  });
}
